import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Package, ShoppingCart, Users, DollarSign, Layers, Settings, TrendingUp, Download, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { AdminProducts } from '../components/admin/AdminProducts';
import { AdminOrders } from '../components/admin/AdminOrders';
import { AdminCategories } from '../components/admin/AdminCategories';
import { AdminAnalytics } from '../components/admin/AdminAnalytics';
import { AdminExport } from '../components/admin/AdminExport';
import { AdminBulkUpload } from '../components/admin/AdminBulkUpload';
import { AdminAdvancedStats } from '../components/admin/AdminAdvancedStats';
import { AdminSettings } from '../components/admin/AdminSettings';
import { AdminContactSubmissions } from '../components/admin/AdminContactSubmissions';
import axios from 'axios';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const Admin = () => {
  const { user, isAdmin, token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
    }
  }, [isAdmin]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/stats/dashboard`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-3xl font-bold mb-4">Access Denied</h2>
          <p className="text-muted-foreground mb-6">You don't have permission to access this page.</p>
          <Button onClick={() => window.history.back()}>Go Back</Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Settings },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'orders', label: 'Orders', icon: ShoppingCart },
    { id: 'categories', label: 'Categories', icon: Layers },
    { id: 'settings', label: 'Site Settings', icon: Settings },
    { id: 'messages', label: 'Messages', icon: Users },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'import', label: 'Import', icon: Upload },
    { id: 'export', label: 'Export', icon: Download }
  ];

  return (
    <div className="min-h-screen py-8 bg-muted/30">
      <div className="container-custom">
        <div className="mb-8">
          <h1 className="font-serif text-4xl font-bold text-foreground mb-2">
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">Welcome back, {user.full_name}</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2"
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Products</CardTitle>
                      <Package className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{stats?.total_products || 0}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                      <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{stats?.total_orders || 0}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {stats?.pending_orders || 0} pending
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
                      <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{stats?.total_users || 0}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {formatPrice(stats?.total_revenue || 0)}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Advanced Stats */}
                <AdminAdvancedStats />

                {/* Quick Info */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Package className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">Admin Dashboard - Full Management</h3>
                        <p className="text-muted-foreground leading-relaxed mb-4">
                          Manage your entire e-commerce platform from this dashboard. All APIs are fully functional and ready for production use.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="success">Products Management ✓</Badge>
                          <Badge variant="success">Orders Tracking ✓</Badge>
                          <Badge variant="success">Category Management ✓</Badge>
                          <Badge variant="success">User Management ✓</Badge>
                          <Badge variant="success">Real-time Stats ✓</Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && <AdminProducts />}

        {/* Orders Tab */}
        {activeTab === 'orders' && <AdminOrders />}

        {/* Categories Tab */}
        {activeTab === 'categories' && <AdminCategories />}

        {/* Site Settings Tab */}
        {activeTab === 'settings' && <AdminSettings />}

        {/* Messages Tab */}
        {activeTab === 'messages' && <AdminContactSubmissions token={token} />}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && <AdminAnalytics />}

        {/* Import Tab */}
        {activeTab === 'import' && <AdminBulkUpload token={token} onSuccess={fetchStats} />}

        {/* Export Tab */}
        {activeTab === 'export' && <AdminExport token={token} />}
      </div>
    </div>
  );
};